import { type Component, createSignal, createResource, Show, For } from 'solid-js'
import { A } from '@solidjs/router'
import { AppRoutes } from '../../../shared/app-routes'
import { Icon } from '@iconify-icon/solid'
import { useNavigate } from '@solidjs/router'
import { useAdminEvents } from '../../../shared/providers/coris/coris-admin-events.provider'
import { useUser } from '../../../shared/providers/user-provider'
import DateTimeInput from '../../../components/shared/components/DateTimeInput'
import TimezoneSelect from '../../../components/shared/components/TimezoneSelect'
import { useNotification } from '../../../shared/notifications'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { presalePoolDatesToPayload } from '../domain/presale-pool-dates'

dayjs.extend(utc)
dayjs.extend(timezone)

interface UploadedFile {
  readonly file: File
  readonly codeCount: number
}

/** Inline toast notification — auto-dismisses after a delay. */
const AdminPresalePoolCreatePage: Component = () => {
  const { validatePresaleCodes, createPresalePool, assignPoolToEvent, getEvents } = useAdminEvents()
  const notification = useNotification()
  const navigate = useNavigate()
  const [step, setStep] = createSignal(1)

  // Step 1 state
  const [uploadedFiles, setUploadedFiles] = createSignal<UploadedFile[]>([])
  const [selectedTimezone, setSelectedTimezone] = createSignal(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [presaleStart, setPresaleStart] = createSignal('')
  const [presaleEnd, setPresaleEnd] = createSignal('')
  const [presaleReveal, setPresaleReveal] = createSignal('')
  const [presaleHide, setPresaleHide] = createSignal('')
  const [generalSale, setGeneralSale] = createSignal('')
  const [isDragging, setIsDragging] = createSignal(false)

  // Hide date mirrors presale end until the user explicitly edits it.
  const [hideTouched, setHideTouched] = createSignal(false)
  const changePresaleEnd = (v: string): void => {
    setPresaleEnd(v)
    if (!hideTouched()) setPresaleHide(v)
  }
  const changePresaleHide = (v: string): void => {
    setHideTouched(true)
    setPresaleHide(v)
  }

  // Step 2 state
  const { organizationSlug } = useUser()
  const [selectedEventIds, setSelectedEventIds] = createSignal<Set<number>>(new Set())
  const [eventSearch, setEventSearch] = createSignal('')

  const [allEvents] = createResource(
    () => organizationSlug(),
    async () => getEvents()
  )

  const filteredEvents = () => {
    const events = allEvents() ?? []
    const q = eventSearch().toLowerCase()
    if (!q) return events
    return events.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        (e.venue?.name ?? '').toLowerCase().includes(q) ||
        (e.venue?.address?.city ?? '').toLowerCase().includes(q)
    )
  }

  const toggleEvent = (id: number): void => {
    const next = new Set(selectedEventIds())
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedEventIds(next)
  }

  // startsAt is a floating calendar date stored at UTC midnight — format in UTC so
  // the day never shifts with the viewer's timezone.
  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

  const formatDateShort = (val: string): string => {
    if (!val) return '—'
    const tz = selectedTimezone()
    return dayjs(val).tz(tz).format('DD/MM/YYYY HH:mm')
  }

  const tzAbbrev = () => {
    const tz = selectedTimezone()
    // Get the short timezone abbreviation (e.g., "CET", "EST", "PST")
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? tz
    )
  }

  let fileInputRef: HTMLInputElement | undefined

  const processFile = async (f: File): Promise<void> => {
    const poolName = f.name.replace(/\.[^.]+$/, '')
    const result = await validatePresaleCodes(f, poolName)
    if (result.valid && result.data) {
      setUploadedFiles((prev) => [...prev, { file: f, codeCount: result.data!.totalCodes }])
      notification.success(<span>{result.data.totalCodes.toLocaleString()} Presale codes successfully uploaded</span>)
    } else {
      notification.error(<span>{result.message ?? 'Failed to validate presale codes'}</span>)
    }
  }

  const handleFileDrop = (e: DragEvent): void => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = e.dataTransfer?.files?.[0]
    if (dropped) processFile(dropped)
  }

  const handleFileSelect = (e: Event): void => {
    const input = e.target as HTMLInputElement
    const selected = input.files?.[0]
    if (selected) processFile(selected)
    input.value = ''
  }

  const removeFile = (index: number): void => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const totalCodes = () => uploadedFiles().reduce((sum, f) => sum + f.codeCount, 0)
  const canNext = () => uploadedFiles().length > 0
  const canSave = () => selectedEventIds().size > 0

  return (
    <div class="flex w-full flex-col min-h-full px-6">
      <div
        class="mx-auto w-full max-w-[1280px] flex flex-col gap-6 py-6"
        classList={{ 'max-w-[704px]!': step() === 1 }}
      >
        {/* Stepper */}
        <div class="flex items-center justify-center gap-2 py-2">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-200"
              classList={{
                'bg-green-600 text-white': step() > 1,
                'bg-primary-600 text-white': step() === 1,
                'bg-primary-50 text-primary-600': step() < 1
              }}
            >
              <Show when={step() > 1} fallback="1">
                <Icon icon="heroicons:check" class="text-[1rem]" />
              </Show>
            </div>
            <span class="text-sm font-medium text-gray-900">Presale Codes and Dates</span>
            <div class="w-12 h-0.5 bg-gray-200" />
          </div>
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-200"
              classList={{
                'bg-primary-600 text-white': step() >= 2,
                'bg-primary-50 text-primary-600': step() < 2
              }}
            >
              2
            </div>
            <span class="text-sm font-medium text-gray-900">Event Assignation</span>
          </div>
        </div>

        {/* Step 1 */}
        <Show when={step() === 1}>
          {/* File upload / file list */}
          <Show
            when={uploadedFiles().length > 0}
            fallback={
              /* Empty state — drop zone */
              <div
                class="px-4 py-8 bg-gray-50 rounded-lg border border-dashed flex flex-col items-center gap-4 transition-colors duration-200 cursor-pointer"
                classList={{
                  'border-primary-400 bg-primary-50': isDragging(),
                  'border-gray-300': !isDragging()
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.txt"
                  class="hidden"
                  onChange={handleFileSelect}
                />
                <div class="text-gray-400">
                  <Icon icon="heroicons:cloud-arrow-up" class="text-[2.5rem]" />
                </div>
                <div class="flex flex-col items-center gap-1">
                  <p class="text-xl font-bold text-gray-900">Drop your file here or click to browse</p>
                  <p class="text-xs font-semibold text-gray-500">Supports .xlsx, .xls, .csv</p>
                </div>
                <button
                  type="button"
                  class="min-w-24 px-3.5 py-2.5 bg-primary-600 text-white rounded-md shadow-sm text-sm font-semibold hover:bg-primary-700 transition-colors duration-150"
                  onClick={(e) => {
                    e.stopPropagation()
                    fileInputRef?.click()
                  }}
                >
                  Choose file
                </button>
              </div>
            }
          >
            {/* Uploaded files card */}
            <div class="bg-white rounded-lg border border-gray-200 px-5 py-5 flex flex-col items-center gap-6">
              <div class="self-stretch flex flex-col gap-6">
                <h2 class="text-xl font-bold text-gray-900">Presale Codes</h2>
                <div class="flex flex-col gap-3">
                  <For each={uploadedFiles()}>
                    {(uploaded, index) => (
                      <div class="p-2 bg-white rounded-lg border border-gray-200 flex items-center gap-3 animate-fade-in-right">
                        {/* CSV icon */}
                        <div class="w-12 h-12 relative shrink-0 rounded overflow-hidden">
                          <div class="absolute inset-0 bg-green-600 rounded" />
                          <div class="absolute right-0 top-0 w-4 h-4 bg-green-200 rounded-bl" />
                          <span class="absolute bottom-1.5 left-1.5 text-white/90 text-xs font-semibold">CSV</span>
                        </div>
                        {/* File info */}
                        <div class="flex-1 flex flex-col gap-0.5 min-w-0">
                          <span class="text-sm font-semibold text-gray-900 truncate">{uploaded.file.name}</span>
                          <span class="text-xs font-medium text-gray-500">
                            {uploaded.codeCount.toLocaleString()} Presale Codes
                          </span>
                        </div>
                        {/* Delete */}
                        <button
                          type="button"
                          class="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-150"
                          onClick={() => removeFile(index())}
                        >
                          <Icon icon="heroicons:trash" class="text-[1rem]" />
                        </button>
                      </div>
                    )}
                  </For>
                </div>
              </div>
              {/* Add file button */}
              <button
                type="button"
                class="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors duration-150"
                onClick={() => fileInputRef?.click()}
              >
                <Icon icon="heroicons:plus" class="text-[1rem]" />
                Add file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                class="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </Show>

          {/* Date settings */}
          <div class="bg-white rounded-lg border border-gray-200 px-4 py-3 flex flex-col gap-3">
            <div class="flex items-center gap-3">
              <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                Dates settings
              </span>
              <div class="ml-auto w-full max-w-[260px] min-w-0">
                <TimezoneSelect value={selectedTimezone()} onValueChange={setSelectedTimezone} />
              </div>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-gray-700">Show Presale unique code</label>
                <DateTimeInput
                  value={presaleReveal()}
                  onValueChange={setPresaleReveal}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-gray-700">Presale start</label>
                <DateTimeInput
                  value={presaleStart()}
                  onValueChange={setPresaleStart}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-gray-700">Presale end</label>
                <DateTimeInput
                  value={presaleEnd()}
                  onValueChange={changePresaleEnd}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-gray-700">General Sale Date</label>
                <DateTimeInput
                  value={generalSale()}
                  onValueChange={setGeneralSale}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-gray-700">Hide Presale unique code</label>
                <DateTimeInput
                  value={presaleHide()}
                  onValueChange={changePresaleHide}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
            </div>
          </div>
        </Show>

        {/* Step 2 — full width */}
        <Show when={step() === 2}>
          {/* Single card: summary pills header + unified events table */}
          <div class="bg-white rounded-lg border border-gray-200 px-5 py-4 flex flex-col gap-4">
            <div class="flex flex-col gap-3">
              <div class="flex flex-wrap items-center gap-2">
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[80px]">
                  <span class="text-base font-bold text-gray-900 tabular-nums leading-tight">
                    {totalCodes().toLocaleString()}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Codes</span>
                </div>
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[80px]">
                  <span class="text-base font-bold text-gray-900 tabular-nums leading-tight">
                    {selectedEventIds().size}/{(allEvents() ?? []).length}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Selected</span>
                </div>
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[140px]">
                  <span class="text-sm font-bold text-gray-900 tabular-nums leading-tight">
                    {formatDateShort(presaleReveal())}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Reveal</span>
                </div>
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[140px]">
                  <span class="text-sm font-bold text-gray-900 tabular-nums leading-tight">
                    {formatDateShort(presaleStart())}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Start</span>
                </div>
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[140px]">
                  <span class="text-sm font-bold text-gray-900 tabular-nums leading-tight">
                    {formatDateShort(presaleEnd())}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">End</span>
                </div>
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[140px]">
                  <span class="text-sm font-bold text-gray-900 tabular-nums leading-tight">
                    {formatDateShort(generalSale())}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">General sale</span>
                </div>
                <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[140px]">
                  <span class="text-sm font-bold text-gray-900 tabular-nums leading-tight">
                    {formatDateShort(presaleHide())}
                  </span>
                  <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Hide</span>
                </div>
                <span class="text-[10px] uppercase tracking-wide text-gray-400 font-semibold ml-1">{tzAbbrev()}</span>
              </div>
              <div class="min-w-0">
                <h2 class="text-base font-bold text-gray-900 leading-tight">Selected events</h2>
                <span class="text-xs text-gray-500">These dates will apply to all selected events</span>
              </div>
            </div>

            <div class="border-t border-gray-100" />

            <div class="flex items-center gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div class="text-gray-400">
                <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
              </div>
              <input
                type="text"
                placeholder="Search events..."
                value={eventSearch()}
                onInput={(e) => setEventSearch(e.currentTarget.value)}
                class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>

            <div class="max-h-[480px] overflow-y-auto flex flex-col gap-1">
              <Show
                when={!allEvents.loading}
                fallback={<div class="py-8 text-center text-sm text-gray-400">Loading events...</div>}
              >
                <Show
                  when={filteredEvents().length > 0}
                  fallback={<div class="py-8 text-center text-sm text-gray-400">No events found.</div>}
                >
                  <For each={filteredEvents()}>
                    {(event) => {
                      const isSelected = (): boolean => selectedEventIds().has(event.id)
                      return (
                        <div
                          class="p-3 rounded-lg flex items-center gap-3 transition-colors duration-100"
                          classList={{
                            'is-assigned bg-primary-50 hover:bg-primary-100': isSelected(),
                            'bg-gray-50 hover:bg-gray-100': !isSelected()
                          }}
                        >
                          <div class="flex-1 min-w-0">
                            <span class="text-sm font-medium text-gray-900 block truncate">{event.title}</span>
                            <span class="text-xs text-gray-500 block truncate">
                              {formatDate(event.startsAt)} · {event.venue?.name ?? ''}
                            </span>
                          </div>
                          <Show
                            when={isSelected()}
                            fallback={
                              <button
                                type="button"
                                class="shrink-0 px-3 py-1.5 bg-primary-600 text-white rounded-md text-xs font-semibold hover:bg-primary-700 transition-colors duration-150"
                                onClick={() => toggleEvent(event.id)}
                              >
                                Select
                              </button>
                            }
                          >
                            <button
                              type="button"
                              class="shrink-0 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-md text-xs font-semibold hover:border-red-300 hover:text-red-600 transition-colors duration-150"
                              onClick={() => toggleEvent(event.id)}
                            >
                              Unselect
                            </button>
                          </Show>
                        </div>
                      )
                    }}
                  </For>
                </Show>
              </Show>
            </div>
          </div>
        </Show>

        {/* Footer actions */}
        <div class="flex justify-end gap-4 py-4">
          <Show when={step() === 2}>
            <button
              type="button"
              class="min-w-24 px-3.5 py-2.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center transition-colors duration-150"
              onClick={() => setStep(1)}
            >
              Back
            </button>
          </Show>
          <A
            href={AppRoutes.AdminEvents()}
            class="min-w-24 px-3.5 py-2.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center transition-colors duration-150"
          >
            Cancel
          </A>
          <Show
            when={step() === 1}
            fallback={
              <button
                type="button"
                class="min-w-24 px-3.5 py-2.5 rounded-md text-sm font-semibold text-center transition-colors duration-150"
                classList={{
                  'bg-primary-600 text-white hover:bg-primary-700 shadow-sm': canSave(),
                  'bg-primary-600 text-white opacity-40 cursor-not-allowed': !canSave()
                }}
                disabled={!canSave()}
                onClick={() => {
                  const files = uploadedFiles()
                  const eventIds = [...selectedEventIds()]
                  if (files.length === 0 || eventIds.length === 0) return

                  notification.progress(<span>Creating presale unique codes...</span>, {
                    task: async () => {
                      // Upload each file as a pool, then assign to selected events.
                      // Use allSettled with a concurrency limit of 5 to collect per-event
                      // failures without exhausting the backend's DB connection pool.
                      const CONCURRENCY = 5
                      let totalSuccess = 0
                      const failures: { eventId: number; reason: string }[] = []
                      const tz = selectedTimezone()
                      const poolDates = presalePoolDatesToPayload(
                        {
                          presaleStart: presaleStart(),
                          presaleEnd: presaleEnd(),
                          presaleReveal: presaleReveal(),
                          presaleHide: presaleHide(),
                          generalSaleStart: generalSale()
                        },
                        tz
                      )
                      for (const { file } of files) {
                        const poolName = file.name.replace(/\.[^.]+$/, '')
                        const { poolId } = await createPresalePool(file, poolName, poolDates)
                        for (let i = 0; i < eventIds.length; i += CONCURRENCY) {
                          const batch = eventIds.slice(i, i + CONCURRENCY)
                          const results = await Promise.allSettled(batch.map((eid) => assignPoolToEvent(poolId, eid)))
                          results.forEach((r, idx) => {
                            if (r.status === 'fulfilled') totalSuccess += 1
                            else
                              failures.push({
                                eventId: batch[idx],
                                reason: r.reason instanceof Error ? r.reason.message : String(r.reason)
                              })
                          })
                        }
                      }
                      return { totalSuccess, failures }
                    },
                    onSuccess: (result) => {
                      navigate(AppRoutes.AdminEvents())
                      return (
                        <Show
                          when={result.failures.length === 0}
                          fallback={
                            <span>
                              Pool created. Assigned to {result.totalSuccess} event
                              {result.totalSuccess !== 1 ? 's' : ''}, {result.failures.length} failed.
                            </span>
                          }
                        >
                          <span>Pool created and assigned to {result.totalSuccess} events</span>
                        </Show>
                      )
                    },
                    onError: (err) => (
                      <span>Failed to create pool: {err instanceof Error ? err.message : 'Unknown error'}</span>
                    )
                  })
                }}
              >
                Create Presale Unique Codes
              </button>
            }
          >
            <button
              type="button"
              class="min-w-24 px-3.5 py-2.5 rounded-md text-sm font-semibold text-center transition-colors duration-150"
              classList={{
                'bg-primary-600 text-white hover:bg-primary-700 shadow-sm': canNext(),
                'bg-primary-600 text-white opacity-40 cursor-not-allowed': !canNext()
              }}
              disabled={!canNext()}
              onClick={() => setStep(2)}
            >
              Next
            </button>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default AdminPresalePoolCreatePage
